import type * as React from 'react'

// Epic 52 Phase 03 — Dual-Size Batch B
// (.claude/epics/52-token-and-icon-foundation/reference/ICON-NORMALISATION.md §(b)/(c))
// on `icon / Spinner` (16px export at node 102:1722; 12px export at node 102:935, per
// D-ICON-1 not exported — the 16px node is the single authored source).
//
// NOTE (Phase 00 contract gap, still open): reference/ICON-NORMALISATION.md §(c) specifies a
// shared `SvgIconProps` from './types.ts'. That file has no owning task in any phase file yet
// (flagged in PHASE-00's Notes as an open gap). This component must not create `./types.ts`
// unprompted, so the prop type is inlined here rather than imported. Once `./types.ts` exists,
// this local type should be replaced with an import from it to match every other icon component.
type SvgIconProps = Omit<React.SVGProps<SVGSVGElement>, 'children'> & {
  /** Accessible name. Omit for a purely decorative icon (default: aria-hidden). */
  title?: string
  /** Rendered size in px. Defaults to 16 — the artwork's native/authored size. */
  size?: 12 | 16
}

// D-ICON-1 (2026-08-25): the 12px and 16px `Spinner` exports were measured coordinate-by-coordinate
// (202 coordinates compared) and found to be a uniform 4/3 scale of one another, ratio spread
// 0.000013 — equal to within float rounding. Per the owner decision this single component keeps
// the 16px artwork and viewBox (more coordinate precision than the 12px export) and lets the
// `size` prop drive only the rendered width/height attributes; the viewBox stays "0 0 16 16"
// regardless of `size`, so the 12px render is the SVG user agent uniformly scaling this same
// path data by 12/16 — mathematically identical to what the separate 12px artwork was (the two
// differed only by that same uniform scale).
export function IconSpinner({ title, size = 16, ...rest }: SvgIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <g>
        <path
          d="M8 11.25C8.41421 11.25 8.75 11.5858 8.75 12V14C8.75 14.4142 8.41421 14.75 8 14.75C7.58579 14.75 7.25 14.4142 7.25 14V12C7.25 11.5858 7.58579 11.25 8 11.25Z"
          fill="currentColor"
        />
        <path
          d="M4.64193 10.2982C4.93485 10.0056 5.41034 10.0054 5.70312 10.2982C5.99519 10.5908 5.99502 11.0653 5.70312 11.3581L4.28776 12.7734C3.99501 13.0657 3.52064 13.0656 3.22786 12.7734C2.93497 12.4805 2.93497 12.0051 3.22786 11.7122L4.64193 10.2982Z"
          fill="currentColor"
        />
        <path
          d="M10.2982 10.2982C10.591 10.0054 11.0652 10.0056 11.3581 10.2982L12.7734 11.7122C13.066 12.0052 13.0662 12.4807 12.7734 12.7734C12.4807 13.0662 12.0052 13.066 11.7122 12.7734L10.2982 11.3581C10.0056 11.0652 10.0054 10.591 10.2982 10.2982Z"
          fill="currentColor"
        />
        <path
          d="M4 7.25C4.41421 7.25 4.75 7.58579 4.75 8C4.75 8.41421 4.41421 8.75 4 8.75H2C1.58579 8.75 1.25 8.41421 1.25 8C1.25 7.58579 1.58579 7.25 2 7.25H4Z"
          fill="currentColor"
        />
        <path
          d="M14 7.25C14.4142 7.25 14.75 7.58579 14.75 8C14.75 8.41421 14.4142 8.75 14 8.75H12C11.5858 8.75 11.25 8.41421 11.25 8C11.25 7.58579 11.5858 7.25 12 7.25H14Z"
          fill="currentColor"
        />
        <path
          d="M3.22786 3.22786C3.52076 2.93497 3.99487 2.93497 4.28776 3.22786L5.70312 4.64193C5.9953 4.93488 5.99578 5.41047 5.70312 5.70312C5.41047 5.99578 4.93488 5.9953 4.64193 5.70312L3.22786 4.28776C2.93497 3.99487 2.93497 3.52076 3.22786 3.22786Z"
          fill="currentColor"
        />
        <path
          d="M8 1.25C8.41421 1.25 8.75 1.58579 8.75 2V4C8.75 4.41421 8.41421 4.75 8 4.75C7.58579 4.75 7.25 4.41421 7.25 4V2C7.25 1.58579 7.58579 1.25 8 1.25Z"
          fill="currentColor"
        />
      </g>
    </svg>
  )
}
